/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";
import {toBoolean, toInteger, toIntFlag} from "../../../../../utils/formValueUtils";

/**
 * HOC for the partner form
 */
class InstitutionsForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            institutionId: null,
            institutionName: '',
            // main partner fields
            partnerId: null,
            partnerName: '',
            // the disabled url
            disabledUrl: '',
            // the user consent url
            userConsentUrl: '/UserConsent',
            // values for enabled dropdown
            enabled: '',
            enabledName: '',
            // values for school aware dropdown
            schoolAware: '',
            schoolAwareName: '',
            username: '',
            password: '',
            // values for school aware dropdown
            masterConsent: '',
            masterConsentName: '',
            macKey: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'institutionName',
                    placeholder: 'Please enter institution name here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Partner *',
                    inputName: 'partnerId',
                    optionValueName: 'partnerId',
                    optionLabelName: 'partnerName',
                    required: true,
                    path: '/institutions',
                    valueType: 'partners'
                },
                {
                    inputType: 'textUrl',
                    labelName: 'Disabled Url *',
                    inputName: 'disabledUrl',
                    placeholder: 'Please enter Disabled Url here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'User Consent Url',
                    inputName: 'userConsentUrl',
                    placeholder: 'Please enter User Consent Url here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Enabled *',
                    inputName: 'enabled',
                    optionValueName: 'enabled',
                    optionLabelName: 'enabledName',
                    required: true,
                    path: null,
                    valueType: 'basic'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'School Aware *',
                    inputName: 'schoolAware',
                    optionValueName: 'schoolAware',
                    optionLabelName: 'schoolAwareName',
                    required: true,
                    path: null,
                    valueType: 'basic'
                },
                {
                    inputType: 'text',
                    labelName: 'Username *',
                    inputName: 'username',
                    placeholder: 'Please enter Username here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textPassword',
                    labelName: 'Password *',
                    inputName: 'password',
                    placeholder: 'Please enter Password here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Master User Consent *',
                    inputName: 'masterConsent',
                    optionValueName: 'masterConsent',
                    optionLabelName: 'masterConsentName',
                    required: true,
                    path: null,
                    valueType: 'basic'
                },
                {
                    inputType: 'text',
                    labelName: 'Mac Key',
                    inputName: 'macKey',
                    placeholder: 'Please enter Mac Key here',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const institutionId = this.props.match.params.id;

        // set icons to the header
        if (institutionId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'institutions',
                pageName: "Institutions",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/institutions",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'institutions',
                pageName: "Institutions",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/institutions",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deletePartner);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    componentDidMount = async () => {
        // get parameter from URL
        const institutionId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create institution
        if (institutionId !== undefined) {
            // set a token to unsubscribe the async call
            const cancelToken = axios.CancelToken;
            const src = cancelToken.source();

            const url = '/institutions?search=%7BinstitutionId:' + institutionId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: src.token})
                .then(res => {
                    const data = res.data[0];

                    oldState.institutionId = (data.institutionId !== undefined) ? data.institutionId : null;
                    oldState.institutionName = (data.institutionName !== undefined) ? data.institutionName : null;
                    oldState.partnerId = (data.partnerId !== undefined) ? data.partnerId : null;
                    oldState.partnerName = (data.partnerName !== undefined) ? data.partnerName : null;
                    oldState.disabledUrl = (data.disabledUrl !== undefined) ? data.disabledUrl : '';
                    oldState.userConsentUrl = (data.userConsentUrl !== undefined) ? data.userConsentUrl : '';
                    oldState.enabled = (data.enabled !== undefined) ? data.enabled : '';
                    oldState.schoolAware = (data.schoolAware !== undefined) ? data.schoolAware : '';
                    oldState.username = (data.username !== undefined) ? data.username : '';
                    oldState.password = (data.userPswrd !== undefined) ? data.userPswrd : '';
                    oldState.masterConsent = (data.masterConsent !== undefined) ? data.masterConsent : '' ;
                    oldState.macKey = (data.macKey !== undefined) ? data.macKey : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                }).catch(err => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(err);
                });

            src.cancel();
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters when unmount the component
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deletePartner);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
    };

    /**
     * update the state when user inout a value
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submit
     */
    handleSubmit = () => {
        let macKey = (this.state.macKey === '') ? null : this.state.macKey;

        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.institutionId !== null) {
            data = {
                institutionId: this.state.institutionId,
                institutionName: this.state.institutionName,
                partnerId: toInteger(this.state.partnerId),
                disabledUrl: this.state.disabledUrl,
                userConsentUrl: this.state.userConsentUrl,
                enabled: toBoolean(this.state.enabled),
                schoolAware: toBoolean(this.state.schoolAware),
                username: this.state.username,
                password: this.state.password,
                masterConsent: toBoolean(this.state.masterConsent),
                macKey: macKey,
            };
        } else {
            data = {
                institutionName: this.state.institutionName,
                partnerId: toInteger(this.state.partnerId),
                disabledUrl: this.state.disabledUrl,
                userConsentUrl: this.state.userConsentUrl,
                enabled: toBoolean(this.state.enabled),
                schoolAware: toBoolean(this.state.schoolAware),
                username: this.state.username,
                password: this.state.password,
                masterConsent: toBoolean(this.state.masterConsent),
                macKey: macKey,
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.institutionId !== null) {
                this.updateInstitution(data);
            } else {
                this.createInstitution(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - the input name
     * @param value - the input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'institutionName':
                if (value === '')
                    error[key] = "Institution Name is Required!";
                else if (value === ' ')
                    error[key] = "Institution Name can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Institution Name can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'partnerId':
                if (this.state.partnerId === '' || this.state.partnerId === null)
                    error[key] = "Please select a Partner!";
                else
                    error[key] = "";
                break;
            case 'disabledUrl':
                if (value === '')
                    error[key] = "Disabled URL is Required!";
                else if (value === ' ')
                    error[key] = "Disabled URL can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Disabled Url can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "Disabled URL must begin with with http://, https:// or / ";
                break;
            case 'enabled':
                // if (this.state.enabled === '')
                //     error[key] = "Please select an enabled!";
                // else
                    error[key] = "";
                break;
            case 'schoolAware':
                // if (this.state.schoolAware === '')
                //     error[key] = "Please select a school aware!";
                // else
                    error[key] = "";
                break;
            case 'username':
                if (value === '')
                    error[key] = "Username is Required!";
                else if (value === ' ')
                    error[key] = "Username can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Username can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'password':
                if (this.state.institutionId === null) {
                    if (value === '')
                        error[key] = "Password is Required!";
                    else if (value === ' ')
                        error[key] = "Password can't be an empty space!";
                    else if ((new TextEncoder().encode(value).length > 32))
                        error[key] = "Password can't have more than 32 characters!";
                    else
                        error[key] = "";
                } else if (value === ' ') {
                    error[key] = "Password can't be an empty space!";
                } else if (value !== '' && (new TextEncoder().encode(value).length > 32)) {
                    error[key] = "Password can't have more than 32 characters!";
                } else {
                    error[key] = "";
                }
                break;
            case 'masterConsent':
                // if (this.state.masterConsent === '')
                //     error[key] = "Please select an master user consent!";
                // else
                    error[key] = "";
                break;
            case 'macKey':
                if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Mac Key can't have more than 128 characters!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create institution
     * @param data - the data for create new institution
     */
    createInstitution = async (data) => {
        // set a token to unsubscribe the async call
        const cancelToken = axios.CancelToken;
        const src = cancelToken.source();

        await API.post('/institutions',
            {
                "institutionName": data.institutionName,
                "partnerId": data.partnerId,
                "disabledUrl": data.disabledUrl,
                "userConsentUrl": data.userConsentUrl,
                "enabled": data.enabled,
                "schoolAware": data.schoolAware,
                "username": data.username,
                "userPswrd": data.password === '' ? null : data.password,
                "masterConsent": data.masterConsent,
                "macKey": data.macKey
            }, {headers: {Authorization: this.state.token}, cancelToken: src.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the institution");
            this.props.history.push('/home/institutions');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });

        src.cancel();
    };

    /**
     * update partner
     * @param data - updated partner data
     */
    updateInstitution = async (data) => {
        // set a token to unsubscribe the async call
        const cancelToken = axios.CancelToken;
        const src = cancelToken.source();

        await API.put('/institutions',
            {
                "institutionId": data.institutionId,
                "institutionName": data.institutionName,
                "partnerId": data.partnerId,
                "disabledUrl": data.disabledUrl,
                "userConsentUrl": data.userConsentUrl,
                "enabled": data.enabled,
                "schoolAware": data.schoolAware,
                "username": data.username,
                "userPswrd": data.password === '' ? null : data.password,
                "masterConsent": data.masterConsent,
                "macKey": data.macKey
            },{headers: {Authorization: this.state.token}, cancelToken: src.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the institution");
            this.props.history.push('/home/institutions');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });

        src.cancel();
    };

    /**
     * display the warning message by emitting an event emitter
     */
    displayDeleteWarningMessage = () => {
        if (this.state.institutionId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'institutions',
                name: this.state.institutionName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the partner",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete partner
     */
    deletePartner = async (data) => {
        if (data && (this.props.match.path === "/home/institutions/:id")) {
            // set a token to unsubscribe the async call
            const cancelToken = axios.CancelToken;
            const src = cancelToken.source();

            await API.delete('/institutions?institutionId=' + this.state.institutionId, {headers: {Authorization: this.state.token}, cancelToken: src.token})
                .then(res => {
                    const msg = "Successfully Deleted the institution " + this.state.institutionName;
                    this.setAlert("Success", "Success", msg);
                    this.props.history.push('/home/institutions');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });

            src.cancel();
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Institution is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.institutionId = null;
        newState.institutionName = "";
        newState.partnerId = "";
        newState.partnerName = "";
        newState.disabledUrl = "";
        newState.userConsentUrl = "";
        newState.enabled = "";
        newState.enabledName = "";
        newState.schoolAware = "";
        newState.schoolAwareName = "";
        newState.username = "";
        newState.password = "";
        newState.masterConsent = "";
        newState.macKey = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/institutions');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default InstitutionsForm;
